// Detail-page header: back navigation, the shared PageHeader, and the record's
// actions. Used by the admin detail screens and by both order screens (checkout
// /buy?order=, receipt /purchases/:id), which lead back to /purchases. The back
// control is a ghost Button around a router Link with the app's chevron glyph, never an
// arrow glyph; being a Button, it disappears from the printed receipt. Actions sit
// beside the title only from 1280px: below that they wrap under it, so a long
// account email is never squeezed into a narrow column (PageHeader's own actions
// slot goes side by side from 640px).
// Structure: shadcn-ui/ui apps/v4/registry/new-york-v4/blocks/dashboard-01/components/site-header.tsx (title + actions) and ui/button.tsx (ghost, asChild) — MIT.

import type { ReactNode } from "react";
import { Glyph } from "@/ui";
import { Link } from "react-router-dom";
import { fieldHintClass } from "@ahcut/ui/recipes";
import { Button } from "@/ui";
import { PageHeader } from "@/components/PageHeader";

export function DetailHeader({
  backTo,
  backLabel,
  title,
  lede,
  actions,
  hint,
  hintId,
}: {
  backTo: string;
  backLabel: string;
  title: string;
  lede?: ReactNode;
  /** Buttons acting on the record shown. */
  actions?: ReactNode;
  /** Visible reason under the actions (e.g. why one is disabled). */
  hint?: ReactNode;
  /** Id for `hint`, referenced by the disabled button's aria-describedby. */
  hintId?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="-ml-2 self-start px-2 text-fg-1 hover:text-fg-0"
      >
        <Link to={backTo}>
          <Glyph name="chevronLeft" />
          {backLabel}
        </Link>
      </Button>
      {/* items-start: the h1 and the buttons share a top line; a hint hangs below the buttons. */}
      <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between xl:gap-8">
        <div className="min-w-0 [&_h1]:break-words">
          <PageHeader title={title} lede={lede} />
        </div>
        {actions && (
          <div className="flex min-w-0 flex-col gap-2 xl:shrink-0 xl:items-end">
            <div className="flex flex-wrap items-center gap-3">{actions}</div>
            {hint && (
              <p id={hintId} className={fieldHintClass}>
                {hint}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
